import { requestPayment } from 'Cloud/Application/Payment';
import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getFeatureAbOverquota } from 'reactApp/modules/features/features.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { Tariff } from 'reactApp/types/Billing';
import { Screen } from 'reactApp/ui/Screen/Screen';
import { TariffCard } from 'reactApp/ui/TariffCard/TariffCard';
import { sendGa } from 'reactApp/utils/ga';
import { EPaymentGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { isYearPeriod } from 'reactApp/utils/Period';

import styles from './OverquotaPopup.css';

export const OverquotaPopup = memo(
    ({ tariff, onClose, isNewOverquota = false }: { tariff: Tariff; onClose: () => void; isNewOverquota?: boolean }): ReactElement => {
        let feature = useSelector(getFeatureAbOverquota);
        if (isNewOverquota) {
            feature = 'b';
        }
        const { over } = useSelector(UserSelectors.getCloudSpace);

        const product = tariff.products.find((product) => isYearPeriod(product.period)) || tariff.products[0];

        const sendOverquotaPaymentGa = useCallback(
            (label) => {
                sendPaymentGa({
                    action: EPaymentGa.overquotaBanner,
                    label,
                    size_delete: over.original,
                    tariff: tariff?.space.original,
                    group: feature ?? 'None',
                });
            },
            [feature, over.original, tariff?.space.original]
        );

        const sendNotifyGa = useCallback((label) => {
            sendGa('overquota', 'notify', label);
        }, []);

        const onBuy = useCallback(() => {
            if (!product) {
                return;
            }

            sendNotifyGa('buyclick');
            sendOverquotaPaymentGa('click');
            onClose();

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            requestPayment({
                productId: product.id,
                forceFastCheckout: true,
                onSuccess: () => {
                    sendNotifyGa('success');
                },
            });
        }, [onClose, product, sendNotifyGa]);

        const onCloseScreen = useCallback(() => {
            sendOverquotaPaymentGa('close');
            sendNotifyGa('close');
            onClose();
        }, [onClose, sendNotifyGa, sendOverquotaPaymentGa]);

        const tariffBlock = useMemo(() => {
            return (
                <div className={styles.tariffCardWrapper}>
                    <TariffCard
                        id={product.id}
                        tariffId={tariff.id}
                        flags={tariff.flags}
                        space={product.space}
                        price={product.price}
                        discountPrice={product.discountPrice}
                        onBuyClick={onBuy}
                        showOldPrice={false}
                        period={product.period}
                        isPrimary
                        isVisible
                        withoutTariffIcon
                        modificator="overquota"
                        labelText="Самый выгодный"
                        showMonthlyPrice
                        isOverquotaB={feature === 'b' || feature === 'c'}
                    />
                </div>
            );
        }, [feature, onBuy, product, tariff]);

        useEffect(() => {
            sendOverquotaPaymentGa('view');
            sendNotifyGa('show');
        }, []);

        return (
            <Screen closable={true} onClose={onCloseScreen} vkLogo>
                <div className={classNames(styles.root, styles.root_centered)}>
                    <div className={styles.title}>
                        {feature === 'c' ? 'Скоро часть ваших файлов\nбудет удалена' : 'Ваше Облако переполнено'}
                    </div>
                    <div className={styles.description}>
                        Файлы занимают на <span className={styles.quota}>{over.value}</span> больше места, чем у вас есть.
                        <br />
                        Увеличьте объём Облака или удалите лишнее.
                    </div>
                    {tariffBlock}
                    <div className={styles.footer}>
                        Подробную информацию об ограничениях и последствиях вы можете посмотреть{' '}
                        <a href="/LA/" target="_blank">
                            здесь
                        </a>
                    </div>
                </div>
            </Screen>
        );
    }
);

OverquotaPopup.displayName = 'OverquotaPopup';
